import React, { type ReactNode } from 'react';
import { findKey, isFunction, matches, stubFalse, without } from 'lodash-es';
import type { Observable } from 'rxjs';
import { combineLatest, map, of } from 'rxjs';
import type { AirConfig, DataType, LxnValue, SoundConfig, VibrationConfig } from '@/graphql/types';
import { ContentTab, GraphType, SwarmType } from '../enums';
import { distanceConverter, replaceDistanceUnit, roundToN } from '../utils/formatting';
import { applyNamingConvention, namingConventions$ } from '../utils/naming-conventions';
import { LnLegendTable } from './graph-elements/sound-plugin/LnLegendTable';
import { getSoundUnit$ } from './graph-elements/sound-plugin/unit-formatter';
import { DUST_GRAPH_DATA_TYPES } from './constants';
import { DATA_TYPE, ScaleType } from './enums';
import type { DataSet } from './dataset';
import type { ValueTimeGraph } from './graph';
import type { Profile } from '../profile';

interface UnitGetterArgs {
    dataSet?: DataSet;
    profile: Profile;
}

export function getDataTypeKey(value: string): DataType | undefined {
    return findKey(DATA_TYPE, matches(value)) as DataType | undefined;
}

export const dustGraphDataTypesWithoutPM10 = without(DUST_GRAPH_DATA_TYPES, DATA_TYPE.PM10);
export const dustGraphDataTypesWithoutPM2p5 = without(DUST_GRAPH_DATA_TYPES, DATA_TYPE.PM2P5);

type Config = AirConfig | SoundConfig | VibrationConfig;

type ConfigMatcher = (config: Config) => boolean;

type EventWithConfig = { config: Config };

type LegendExcludeType = string | RegExp;

interface DataTypeInfoOptions {
    key: string;
    title: string | null;
    graphName?: string;
    axisTitle?: string;
    unit: string | null;
    visibleData?: Array<string | RegExp>;
    legendExcludeTypes?: LegendExcludeType[];
    amountAfterDot?: number;
    convertDistance?: boolean;
    valueScaleMin?: number;
    valueScaleInitial?: number;
    valueScaleMax?: number;
    defaultGraphType?: GraphType;
    showFrequencyGraph?: boolean;
    showGuideLineLines?: boolean;
    showVssTable?: boolean;
    showVeffDayGraph?: boolean;
    showVperLines?: boolean;
    frequencyGraphScaleType?: ScaleType;
    contentTab?: ContentTab;
    configMatcherQuery?: Partial<Config> | ConfigMatcher;
    useNewLegend?: boolean;
    followMouseWithLine?: boolean;
    onlyShowToolTipForEvents?: boolean;
    valueNames?: string[];
    getUnit$?: (args: UnitGetterArgs) => Observable<string | null>;
    getLegendTooltipContent$?: (graph: ValueTimeGraph, dataSet: DataSet) => Observable<ReactNode>;
}

class DataTypeInfo {
    declare key: string;

    declare title: string;

    graphName?: string;

    axisTitle?: string;

    declare unit: string | null;

    originalUnit: string | null;

    visibleData?: Array<string | RegExp>;

    legendExcludeTypes?: LegendExcludeType[];

    declare amountAfterDot: number;

    declare convertDistance: boolean;

    declare valueScaleMin: number;

    valueScaleInitial?: number;

    valueScaleMax?: number;

    declare defaultGraphType: GraphType;

    declare showFrequencyGraph: boolean;

    declare showGuideLineLines: boolean;

    declare showVssTable: boolean;

    declare showVeffDayGraph: boolean;

    declare showVperLines: boolean;

    declare frequencyGraphScaleType: ScaleType;

    declare contentTab: ContentTab;

    configMatcherQuery?: Partial<Config> | ConfigMatcher;

    configMatcher: ConfigMatcher;

    eventConfigMatcher: (event: EventWithConfig) => boolean;

    useNewLegend?: boolean;

    followMouseWithLine?: boolean;

    onlyShowToolTipForEvents?: boolean;

    valueNames?: string[];

    constructor(options: DataTypeInfoOptions) {
        Object.assign(this, {
            // Defaults:
            convertDistance: false,
            valueScaleMin: 0,
            defaultGraphType: GraphType.Bar,
            showFrequencyGraph: false,
            showGuideLineLines: false,
            showVssTable: false,
            showVeffDayGraph: false,
            showVperLines: false,
            frequencyGraphScaleType: ScaleType.Linear,
            contentTab: ContentTab.GRAPH,
            ...options,
        });

        this.originalUnit = this.unit;

        // Cached version of `matches(this.configMatcherQuery)(config)`.
        if (isFunction(this.configMatcherQuery)) {
            this.configMatcher = this.configMatcherQuery;
        } else if (this.configMatcherQuery) {
            this.configMatcher = matches(this.configMatcherQuery);
        } else {
            this.configMatcher = stubFalse;
        }

        // Cached version of `matches(this.configMatcherQuery)(event)`.
        this.eventConfigMatcher = (event: EventWithConfig) => this.configMatcher(event.config);
    }

    /**
     * Resolve the unit to display for this data type.
     *
     * Callers that convert a value with `convertDistanceAndFloat` must label it
     * with this rather than the static `unit` property, otherwise a profile using
     * `cm`, `inch` or `mil` renders a converted value next to a `mm` unit.
     *
     * The sound data types resolve their unit from a `DataSet` and override
     * `getUnit$` instead, so they never reach this implementation.
     */
    getUnit(profile: Profile): string | null {
        if (this.convertDistance) {
            return replaceDistanceUnit(this.originalUnit, profile.distanceUnit);
        }
        return this.unit;
    }

    getUnit$({ profile }: UnitGetterArgs): Observable<string | null> {
        return of(this.getUnit(profile));
    }

    getAxisTitle$({ dataSet, profile }: UnitGetterArgs): Observable<string> {
        // Use the axis title if this data type has one.
        if (this.axisTitle) {
            return of(this.axisTitle);
        }

        // Data type has no axis title, we create it from the data title and data unit.
        return combineLatest([namingConventions$, this.getUnit$({ dataSet, profile })]).pipe(
            map(([_namingConventions, unit]) => {
                let text = applyNamingConvention(this.title);
                if (unit) {
                    text += ` [${unit}]`;
                }
                return text;
            })
        );
    }

    getLegendTooltipContent$(graph: ValueTimeGraph, dataSet: DataSet): Observable<ReactNode> {
        return graph.tooltipHelper.data$.pipe(
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            map((data) => fixFloat(dataSet.dataType, data!.element.value))
        );
    }
}

function createPmTilte(pmType: string, interval: string): string {
    return `${pmType === 'pm10' ? 'PM x' : pmType.toUpperCase()} ${interval}`;
}

const pmAmountAfterDot = 2;

const pmList = ['pm10', 'pm2p5'].flatMap((pmType) => [
    {
        key: `${pmType}1m`,
        title: createPmTilte(pmType, '1min'),
        unit: 'μg/m3',
        amountAfterDot: pmAmountAfterDot,
    },
    {
        key: `${pmType}15m`,
        title: createPmTilte(pmType, '15min'),
        unit: 'μg/m3',
        amountAfterDot: pmAmountAfterDot,
    },
    {
        key: `${pmType}60m`,
        title: createPmTilte(pmType, '1hour'),
        unit: 'μg/m3',
        amountAfterDot: pmAmountAfterDot,
    },
    {
        key: `${pmType}600m`,
        title: createPmTilte(pmType, '10hour'),
        unit: 'μg/m3',
        amountAfterDot: pmAmountAfterDot,
    },
    {
        key: `${pmType}1440m`,
        title: createPmTilte(pmType, '24hour'),
        unit: 'μg/m3',
        amountAfterDot: pmAmountAfterDot,
    },
]);

function soundHasIntervalEnabled(intervalName: keyof SoundConfig): ConfigMatcher {
    return function intervalEnabledCheck(config: Config): boolean {
        return (
            config.swarmType === SwarmType.Sound &&
            intervalName in config &&
            (config as SoundConfig)[intervalName] !== 'intervalDisabled'
        );
    };
}

export const dataTypes: readonly DataTypeInfo[] = [
    {
        key: DATA_TYPE.VTOP,
        title: '~~vtop~~',
        graphName: gettext('~~vtop_ext~~/time'),
        unit: 'mm/s',
        visibleData: ['vtop', 'fdom'],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 4,
        convertDistance: true,
        showFrequencyGraph: true,
        showGuideLineLines: true,
        valueScaleInitial: 10,
        valueScaleMax: 300,
        configMatcherQuery: {
            vtopEnabled: true,
            swarmType: SwarmType.Vibration,
        },
    },
    {
        key: DATA_TYPE.ATOP,
        title: '~~atop~~',
        graphName: gettext('~~atop_ext~~/time'),
        unit: 'mm/s²',
        visibleData: ['atop'],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 4,
        convertDistance: true,
        valueScaleInitial: 1300,
        valueScaleMax: 4000,
        configMatcherQuery: {
            atopEnabled: true,
            swarmType: SwarmType.Vibration,
        },
    },
    {
        key: DATA_TYPE.VEFF,
        title: '~~veff_max~~',
        graphName: gettext('~~veff_max_ext~~/time'),
        unit: null,
        visibleData: ['veff', 'vper', 'vmax'],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, 'vector', ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 2,
        convertDistance: false,
        showVssTable: true,
        showVeffDayGraph: true,
        showVperLines: true,
        valueScaleInitial: 10,
        valueScaleMax: 100,
        configMatcherQuery: {
            veffEnabled: true,
            swarmType: SwarmType.Vibration,
        },
    },
    {
        key: DATA_TYPE.VDV,
        title: 'VDV',
        graphName: gettext('VDV/time'),
        unit: 'm/s–1.75',
        visibleData: ['vdv'],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, 'vector', /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 4,
        valueScaleInitial: 10,
        valueScaleMax: 100,
        configMatcherQuery: {
            vdvEnabled: true,
            swarmType: SwarmType.Vibration,
        },
    },
    {
        key: DATA_TYPE.FDOM,
        title: 'Fdom',
        axisTitle: gettext('Dominant frequency [Hz]'),
        graphName: gettext('Frequency/time'),
        unit: 'Hz',
        visibleData: ['fdom', 'vtop'],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 1,
        showFrequencyGraph: true,
        valueScaleInitial: 315.0, // Hz
        valueScaleMax: 315.0, // Hz
        configMatcherQuery: {
            vtopEnabled: true,
            swarmType: SwarmType.Vibration,
        },
    },
    {
        key: DATA_TYPE.PM10,
        title: 'PM x',
        graphName: gettext('Dust/time'),
        unit: 'μg/m3',
        visibleData: [/^pm10/],
        legendExcludeTypes: [
            'x',
            'y',
            'z',
            'vector',
            /^vper/,
            /^pm2p5/,
            ...dustGraphDataTypesWithoutPM10,
        ],
        amountAfterDot: pmAmountAfterDot,
        valueScaleInitial: 50,
        valueScaleMax: 100000,
        configMatcherQuery: { swarmType: SwarmType.Air },
        defaultGraphType: GraphType.Line,
    },
    {
        key: DATA_TYPE.PM2P5,
        title: 'PM2.5',
        graphName: gettext('Dust/time'),
        unit: 'μg/m3',
        visibleData: [/^pm2p5/],
        legendExcludeTypes: [
            'x',
            'y',
            'z',
            'vector',
            /^vper/,
            /^pm10/,
            ...dustGraphDataTypesWithoutPM2p5,
        ],
        amountAfterDot: pmAmountAfterDot,
        valueScaleInitial: 50,
        valueScaleMax: 100000,
        configMatcherQuery: { swarmType: SwarmType.Air },
        defaultGraphType: GraphType.Line,
    },
    {
        key: DATA_TYPE.FLOW,
        title: gettext('Flow'),
        graphName: gettext('Flow/time'),
        unit: 'l/m',
        visibleData: [DATA_TYPE.FLOW],
        legendExcludeTypes: [
            'x',
            'y',
            'z',
            'vector',
            /^vper/,
            /^pm10/,
            /^pm2p5/,
            ...without(DUST_GRAPH_DATA_TYPES, DATA_TYPE.FLOW),
        ],
        amountAfterDot: pmAmountAfterDot,
        valueScaleInitial: 2.5,
        valueScaleMax: 2.5,
        configMatcherQuery: { swarmType: SwarmType.Air },
        defaultGraphType: GraphType.Line,
    },
    {
        key: DATA_TYPE.RH,
        title: gettext('Relative humidity'),
        graphName: gettext('Relative humidity/time'),
        unit: '%',
        visibleData: [DATA_TYPE.RH],
        legendExcludeTypes: [
            'x',
            'y',
            'z',
            'vector',
            /^vper/,
            /^pm10/,
            /^pm2p5/,
            ...without(DUST_GRAPH_DATA_TYPES, DATA_TYPE.RH),
        ],
        amountAfterDot: pmAmountAfterDot,
        valueScaleInitial: 100,
        valueScaleMax: 100,
        configMatcherQuery: { swarmType: SwarmType.Air },
        defaultGraphType: GraphType.Line,
    },
    {
        key: DATA_TYPE.TEMP,
        title: gettext('Temperature'),
        graphName: gettext('Temperature/time'),
        unit: '°C',
        visibleData: [DATA_TYPE.TEMP],
        legendExcludeTypes: [
            'x',
            'y',
            'z',
            'vector',
            /^vper/,
            /^pm10/,
            /^pm2p5/,
            ...without(DUST_GRAPH_DATA_TYPES, DATA_TYPE.TEMP),
        ],
        amountAfterDot: pmAmountAfterDot,
        valueScaleInitial: 50,
        valueScaleMax: 55,
        valueScaleMin: -11,
        configMatcherQuery: { swarmType: SwarmType.Air },
        defaultGraphType: GraphType.Line,
    },
    {
        key: DATA_TYPE.LXEQ,
        title: 'Leq',
        graphName: gettext('LEQ_GRAPH_NAME'),
        unit: 'dB',
        getUnit$({ dataSet }: UnitGetterArgs) {
            if (!dataSet) throw new Error('Missing dataSet for sound unit getter');
            return getSoundUnit$(dataSet);
        },
        visibleData: [DATA_TYPE.LXEQ],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 1,
        convertDistance: false,
        showFrequencyGraph: false,
        showGuideLineLines: false,
        useNewLegend: true,
        followMouseWithLine: true,
        onlyShowToolTipForEvents: true,
        valueScaleInitial: 80,
        valueScaleMax: 140,
        configMatcherQuery: soundHasIntervalEnabled('lxEqInterval'),
        defaultGraphType: GraphType.Step,
    },
    {
        key: DATA_TYPE.LXTMAX,
        title: 'Lmax',
        graphName: gettext('LMAX_GRAPH_NAME'),
        unit: 'dB',
        getUnit$({ dataSet }: UnitGetterArgs) {
            if (!dataSet) throw new Error('Missing dataSet for sound unit getter');
            return getSoundUnit$(dataSet);
        },
        visibleData: [DATA_TYPE.LXTMAX],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 1,
        convertDistance: false,
        showFrequencyGraph: false,
        showGuideLineLines: false,
        useNewLegend: true,
        followMouseWithLine: true,
        onlyShowToolTipForEvents: true,
        valueScaleInitial: 80,
        valueScaleMax: 140,
        configMatcherQuery: soundHasIntervalEnabled('lXtMinMaxInterval'),
        defaultGraphType: GraphType.Step,
    },
    {
        key: DATA_TYPE.DAILY_VALUES,
        title: gettext('DAILY_VALUES'),
        contentTab: ContentTab.DAILY_VALUES,
        graphName: gettext('DAILY_VALUES'),
        unit: 'dB',
        visibleData: [DATA_TYPE.DAILY_VALUES],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 1,
        convertDistance: false,
        showFrequencyGraph: false,
        showGuideLineLines: false,
        useNewLegend: true,
        followMouseWithLine: true,
        onlyShowToolTipForEvents: true,
        valueScaleInitial: 80,
        valueScaleMax: 140,
        configMatcherQuery: soundHasIntervalEnabled('lxEqInterval'),
        defaultGraphType: GraphType.Step,
    },
    {
        key: DATA_TYPE.LXN,
        title: 'Ln',
        graphName: gettext('Ln'),
        unit: 'dB',
        getUnit$({ dataSet }: UnitGetterArgs) {
            if (!dataSet) throw new Error('Missing dataSet for sound unit getter');
            return getSoundUnit$(dataSet);
        },
        visibleData: [DATA_TYPE.LXN],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 1,
        convertDistance: false,
        showFrequencyGraph: false,
        showGuideLineLines: false,
        useNewLegend: true,
        followMouseWithLine: true,
        onlyShowToolTipForEvents: true,
        valueScaleInitial: 80,
        valueScaleMax: 140,
        configMatcherQuery: soundHasIntervalEnabled('lXnInterval'),
        defaultGraphType: GraphType.Step,
        getLegendTooltipContent$(graph: ValueTimeGraph, _dataSet: DataSet) {
            return graph.tooltipHelper.data$.pipe(
                map((data) => {
                    // Check if the record's data type is 'LXN'. Due to asynchronous execution
                    // of observables, there's a chance that the tooltip handler may switch to
                    // a different data type before the legend content renderer does. If the data
                    // type does not match 'LXN', return null to prevent errors.
                    if (!data || data.element.record.__typename !== 'LXN') {
                        return null;
                    }

                    return (
                        <LnLegendTable
                            lnValues={data.element.record.value as Array<LxnValue>}
                            // eslint-disable-next-line @typescript-eslint/no-use-before-define
                            fixFloat={(v) => fixFloat(DATA_TYPE.LXN, v)}
                        />
                    );
                })
            );
        },
    },
    {
        key: DATA_TYPE.LXPEAK,
        title: 'Lpeak',
        graphName: gettext('Lpeak'),
        unit: 'dB',
        getUnit$({ dataSet }: UnitGetterArgs) {
            if (!dataSet) throw new Error('Missing dataSet for sound unit getter');
            return getSoundUnit$(dataSet);
        },
        visibleData: [DATA_TYPE.LXN],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 1,
        convertDistance: false,
        showFrequencyGraph: false,
        showGuideLineLines: false,
        useNewLegend: true,
        followMouseWithLine: true,
        onlyShowToolTipForEvents: true,
        valueScaleInitial: 80,
        valueScaleMax: 140,
        configMatcherQuery: soundHasIntervalEnabled('lxPeakInterval'),
        defaultGraphType: GraphType.Step,
    },
    {
        key: DATA_TYPE.LDEN,
        title: gettext('LDEN'),
        contentTab: ContentTab.LDEN,
        graphName: gettext('LDEN'),
        unit: 'dB',
        visibleData: ['lXtMax'],
        legendExcludeTypes: [/^pm10/, /^pm2p5/, /^vper/, ...DUST_GRAPH_DATA_TYPES],
        amountAfterDot: 1,
        convertDistance: false,
        showFrequencyGraph: false,
        showGuideLineLines: false,
        useNewLegend: true,
        followMouseWithLine: true,
        onlyShowToolTipForEvents: true,
        valueScaleInitial: 80,
        valueScaleMax: 140,
        configMatcherQuery: soundHasIntervalEnabled('lxEqInterval'),
        defaultGraphType: GraphType.Step,
    },
    // Keep these last as they are no real graph types and thus shouldn't get detected by
    // the auto config detection system.
    // TODO: remove these https://github.com/omnidots/website/issues/6823
    // Unitless and unconverted on purpose: the backend limits behind these declare no unit,
    // and under DIN 4150-2 they are KBFmax/KBFTr, a dimensionless rating. See PR #9971.
    {
        key: 'vper',
        title: '~~vper~~',
        graphName: gettext('~~vper~~ table'),
        valueNames: ['~~a1~~', '~~a2~~', '~~a3~~'],
        unit: null,
        amountAfterDot: 2,
    },
    {
        key: 'vmax',
        title: '~~vmax~~',
        unit: null,
        amountAfterDot: 2,
    },
    {
        key: 'stopCause',
        title: gettext('Cause'),
        unit: '',
    },
    {
        key: 'uploading',
        title: null,
        unit: '',
    },
    {
        key: 'startMessage',
        title: null,
        unit: '',
    },
    {
        key: 'name',
        title: null,
        unit: '',
    },
    ...pmList,
].map((options) => new DataTypeInfo(options));

export function getDataType(key: string): DataTypeInfo {
    const found = dataTypes.find((dataType) => dataType.key === key);

    if (!found) {
        throw new Error(
            `DataTypeInfo ${key} unknown. Did you forget to add it to the 'dataTypes' list? `
        );
    }

    return found;
}

/**
 * Finds the best matching data type for the supplied config.
 * For now, the data type preference is in the order of the
 * dataTypes array.
 *
 * @param config - Config object returned from the API.
 * @return {DataTypeInfo}
 */
export function findBestMatchingDataTypeForConfig(config: Config): DataTypeInfo | undefined {
    return dataTypes.find(({ configMatcher }) => configMatcher(config));
}

export const guideLineSettings: Readonly<Record<string, Partial<DataTypeInfo>>> = Object.freeze({
    SS_4604861: {
        showVssTable: false,
        showVeffDayGraph: false,
        showVperLines: false,
        frequencyGraphScaleType: ScaleType.Logarithmic,
        legendExcludeTypes: [...getDataType(DATA_TYPE.VEFF).legendExcludeTypes!, /^vper/],
    },
    SS_025211: {
        frequencyGraphScaleType: ScaleType.Logarithmic,
    },
});

export function getDataTypeKeyByValue(value: string): DataType | undefined {
    return findKey(DATA_TYPE, matches(value)) as DataType | undefined;
}

export function convertDistanceForDataType(type: string, value: number, profile: Profile): number {
    if (getDataType(type).convertDistance) {
        return distanceConverter(value, profile.distanceUnit);
    }

    return value;
}

function isFloat(val: number): boolean {
    return !!(val % 1);
}

export function fixFloat(type: string, value: number): string {
    if (isFloat(value)) {
        return value.toFixed(getDataType(type).amountAfterDot);
    }

    // This was previously return just value, but that would return a number
    // while the function expects a string return type.
    return String(value);
}

export function convertDistanceAndFloat(
    type: string,
    value: number,
    profile: Profile
): string | number {
    return fixFloat(type, convertDistanceForDataType(type, value, profile));
}

export function convertDistanceAndRoundToTwo(
    type: string,
    value: number,
    profile: Profile
): number {
    return roundToN(convertDistanceForDataType(type, value, profile));
}
